<template>
  <SloCard
    :key="`product-card-${variationSku}`"
    class="product-card"
    :url="url"
    :bodyLink="true"
    @mouseleave.self="closeMenu()"
  >
    <template v-slot:overlay>
      <ProductBadge v-if="discontinued">{{ 'Discontinued' | t }}</ProductBadge>
      <ProductBadge v-if="productLine === 'TruFlush'">
        {{ 'Not available for US and Canada' | t }}
      </ProductBadge>
    </template>
    <template v-slot:image>
      <transition name="fade">
        <div :key="finish ? finish.name : 'default'">
          <img
            v-if="level === 'product' && finish"
            :src="finish.image"
            :alt="`${productTitle} in ${finish.name}`"
            loading="lazy"
            width="233"
            height="233"
          />
          <SloProductImage
            v-else-if="level === 'variation' && variationImage"
            :source="variationImage"
          />
          <SloProductImage v-else :source="productImage" />
        </div>
      </transition>
    </template>
    <template v-slot:actions>
      <ProductActions
        class="product-card-actions"
        v-if="
          showActions &&
          !hoverFinish &&
          (productType === 'flushometer' || productType === 'faucet')
        "
        :open="menuOpen"
        @open="openMenu()"
      >
        <div style="display: contents" v-if="level === 'variation'">
          <ProductAction :href="['/spec-sheet/' + variationSku]">
            {{ 'Download Spec' | t }}
          </ProductAction>
          <ProductAction
            href="#share-spec"
            @click.prevent="$emit('share', { url: `/spec-sheet/${variationSku}` })"
          >
            {{ 'Share Spec' | t }}
          </ProductAction>
        </div>
        <div style="display: contents" v-else>
          <ProductAction :href="['/general-spec/' + productId]">
            {{ 'Download Spec' | t }}
          </ProductAction>
          <ProductAction
            href="#share-spec"
            @click.prevent="$emit('share', { url: `/general-sheet/${productId}` })"
          >
            {{ 'Share Spec' | t }}
          </ProductAction>
        </div>
      </ProductActions>
    </template>
    <template v-slot:title>
      <span v-if="productType !== 'part'" class="product-card-title">
        <ais-highlight
          v-if="result"
          :hit="result"
          :attribute="level === 'variation' ? 'variation_title' : 'product_title'"
        />
        <span v-else v-html="level === 'variation' ? variationTitle : productTitle" />
        <span class="finish-name" v-if="level === 'product' && finish" :key="finish.name">
          {{ 'in' | t }} {{ finish.name | t }}
        </span>
      </span>
      <span v-else class="product-card-title fw-book">
        <ais-highlight
          v-if="result"
          :hit="result"
          attribute="variation_description"
        />
      </span>
    </template>
    <template
      v-if="isOrderableProduct(productType, level)"
      v-slot:add-to-cart
    >
      <SloProductGate :sku="variationSku">
        <template v-slot="slotProps">
          <SloProductMeta :product="slotProps?.product">
            <span class="text-xl">{{ slotProps?.product?.uom }}</span>
          </SloProductMeta>
          <SubscriptionPLPAddToCartButton
            v-if="slotProps?.product?.isSubscribable"
            :product="slotProps?.product"
          />
          <SloAddToCart
            :sku="variationSku"
            :show-number-input="false"
            :list-name="`plp_page_${productType}`"
            add-to-cart-button-fill="solid"
            class="product-card-add-to-cart"
          />
        </template>
        <template v-slot:not-orderable>
          <VariationActions
            :sku="variationSku"
            :product-type="productType"
            :product-title="variationActionsTitle"
            :product-description="variationActionsDescription"
            :channel-advisor-enabled="variationChannelAdvisorEnabled"
            :show-download-model-spec="false"
            where-to-buy-button-fill="outline"
          />
        </template>
      </SloProductGate>
    </template>
    <template v-if="productType !== 'part'">
      <div class="product-card-description">
        <ais-highlight
          v-if="result"
          :hit="result"
          :attribute="getHighlightAttribute"
        />
        <div
          style="display: contents"
          v-else
          v-html="getDescription"
        />
      </div>
      <div v-if="level === 'product'" class="product-card-specs flex items-center flex-wrap gap-sm">
        <span v-if="productLowestPrice">
          {{ 'As low as' | t }} {{ productLowestPrice | usd }}
        </span>
        <div class="product-card-purchase-badge-wrapper ml-auto">
          <SloIcon v-if="variationMarketplace" name="CartShopping" size="lg" class="c-gray-600" />
          <span v-if="variationMarketplace" class="product-card-purchase-badge">
            {{ 'Available for purchase' | t }}
          </span>
        </div>
      </div>
      <div class="product-card-specs" v-if="level === 'variation'">
        <span v-if="variationListPrice">{{ 'List Price' | t }} {{ variationListPrice | usd }}</span>
        <span class="product-material-number">
          {{ 'Code' | t }}
          <ais-highlight :hit="result" attribute="variation_sku"></ais-highlight>
        </span>
      </div>
      <ProductFinishes
        v-if="finishes.length > 0 && level === 'product'"
        :finishes="finishes"
        :finish="finish"
        v-on:product-finish-change="setSelectedFinish($event)"
        @finish-mouseenter="hoverFinish = true"
        @finish-mouseleave="handleFinishMouseLeave"
      />
    </template>
    <template v-else>
      <div class="product-card-part-info">
        <ais-highlight v-if="result" :hit="result" attribute="variation_part_number" /> -
        <ais-highlight v-if="result" :hit="result" attribute="variation_sku" />
      </div>
    </template>
  </SloCard>
</template>

<script>
import ProductFinishes from './ProductFinishes.vue';
import ProductActions from './ProductActions.vue';
import ProductAction from './ProductAction.vue';
import ProductBadge from './ProductBadge.vue';
import SloProductGate from '../../lib/components/SloProductGate.vue';
import SloProductImage from '../../lib/components/SloProductImage.vue';
import SloProductMeta from '../../lib/components/SloProductMeta.vue';
import SloCard from '../../lib/components/SloCard.vue';
import SloAddToCart from '../../lib/components/SloAddToCart.vue';
import SloIcon from '../../lib/components/SloIcon.vue';
import SloButton from '../../lib/components/SloButton.vue';
import SubscriptionPLPAddToCartButton from '../marketplace/components/SubscriptionPLPAddToCartButton.vue';

import {isArray} from '../main/utils';
import {sortFinishes, getFinishClassName} from './utils/finish';
import {mapGetters, mapState} from 'vuex';

import usd from './usd.filter';
import t from '../main/i18n';
import VariationActions from '../products/components/VariationActions.vue';

export default {
  components: {
    VariationActions,
    ProductFinishes,
    ProductActions,
    ProductAction,
    ProductBadge,
    SloProductGate,
    SloProductMeta,
    SloProductImage,
    SloCard,
    SloAddToCart,
    SloIcon,
    SloButton,
    SubscriptionPLPAddToCartButton
  },
  props: {
    showActions: {type: Boolean, default: true},
    result: Object,
    level: {type: String, default: 'product'},
    discontinued: {type: Boolean, default: false},
    productFinishes: {
      type: [Array, String],
      default: function() {
        return [];
      }
    },
    defaultFinish: {type: String, default: null},
    productType: String,
    productId: [Number, String],
    productImage: String,
    productLine: String,
    productLowestPrice: Number,
    productTitle: String,
    productUrl: String,
    productDescription: String,
    variationDescription: String,
    variationImage: String,
    variationSku: String,
    variationListPrice: Number,
    variationTitle: String,
    variationPartNumber: String,
    variationChannelAdvisorEnabled: Boolean,
    variationMarketplace: Boolean
  },
  filters: {
    t,
    usd
  },
  data() {
    return {
      hoverFinish: false,
      menuOpen: false,
      selectedFinish: null
    };
  },
  methods: {
    addToDesignSpec(params) {
      const useSrc =
        params.thumbnail &&
        (params.thumbnail.indexOf('/') === 0 || params.thumbnail.indexOf('http') === 0);
      const thumbnail = useSrc
        ? `<img src="${params.thumbnail}" style="width:152px;height:152px;margin:auto 0;" width="152" height="152">`
        : `<div style="width:152px;height:152px;margin:auto 0;">${params.thumbnail}</div>`;

      if (this.projects.length === 0) {
        this.$modal.show({
          name: 'DesignSpecCreateProject',
          onCreate: projectId => {
            this.$modal.show(
              Object.assign({name: 'DesignSpecAddToProject'}, params, {
                projectId
              })
            );
          }
        });
      } else {
        this.$modal.show(
          Object.assign({name: 'DesignSpecAddToProject'}, params, {
            onAdd: opts => {
              const toast = this.$snotify.html(
                `<div style="display:flex;">
                  <div class="overlay">
                    <button class="icon" data-dismiss="">
                      <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"/></svg>
                    </button>
                  </div>
                  ${thumbnail}
                  <div style="flex:1 1 auto;margin-left:8px;display:flex;flex-direction:column;align-items:space-between;padding:8px;">
                    <p class="message" style="color:#006BB6;"><svg aria-hidden="true" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z"></path></svg>Added to DesignSpec</a>
                     <p>${opts.product || t('Product') + ' ' + t('has been added to')} <strong>${opts.name}</strong></p>
                    <a href="${opts.url}" style="color:#006BB6;">${t('Open in DesignSpec')}</a>
                  </div>
                </div>`
              );

              toast.on('click', t => {
                if (
                  event.target &&
                  typeof event.target.dataset.dismiss !== 'undefined'
                ) {
                  this.$snotify.remove(t.id);
                } else {
                  window.location = opts.url;
                }
              });
            }
          })
        );
      }
    },
    isOrderableProduct(productType, level) {
      return level === 'variation' || productType === 'part';
    },
    openMenu() {
      this.menuOpen = true;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    setSelectedFinish(finish) {
      this.selectedFinish = finish;
    },
    handleFinishMouseLeave() {
      this.hoverFinish = false;
      if (this.defaultFinish) {
        this.selectedFinish = this.finishes.find(
          finish => finish.name === this.defaultFinish
        );
      }
    }
  },
  watch: {
    defaultFinish: {
      handler() {
        this.selectedFinish = this.finishes.find(
          finish => finish.name === this.defaultFinish
        );
      },
      immediate: true
    },
    productFinishes: function() {
      if (this.defaultFinish) {
        this.selectedFinish = this.finishes.find(
          finish => finish.name === this.defaultFinish
        );
      }
    }
  },
  computed: {
    ...mapState('designspec', ['projects']),
    ...mapGetters('auth', ['authenticated']),
    variationActionsTitle() {
      if (this.productType === 'part' && this.result?.variation_part_number) {
        return this.result?.variation_part_number;
      }

      return this.variationTitle || this.productTitle;
    },
    getDescription() {
      if (this.productType === 'sink') {
        return this.productDescription;
      }

      return this.level === 'variation' ? this.variationDescription : this.productDescription;
    },
    getHighlightAttribute() {
      if (this.productType === 'sink') {
        return 'product_description';
      }

      return this.level === 'variation' ? 'variation_description' : 'product_description';
    },
    variationActionsDescription() {
      return this.variationDescription || this.productDescription;
    },
    finish() {
      if (this.selectedFinish) {
        return this.selectedFinish;
      } else if (this.defaultFinish) {
        const defaultFinish = this.finishes.find(
          finish => finish.name === this.defaultFinish
        );
        return defaultFinish;
      } else if (this.finishes.length > 0) {
        return this.finishes[0];
      } else {
        return null;
      }
    },
    finishes() {
      let values = this.productFinishes;
      if (typeof values === 'string') values = JSON.parse(values);
      if (!isArray(values)) return [];

      values = values.reduce((result, item) => {
        let json = item;
        if (typeof item === 'string') json = JSON.parse(item);
        const [name, image] = Object.entries(json)[0];
        return result.concat({
          name,
          image,
          key: getFinishClassName(name)
        });
      }, []);

      return sortFinishes(values);
    },
    url() {
      const url = new URL(this.productUrl, window.location.origin);

      if (this.level === 'variation') {
        url.searchParams.set('sku', this.variationSku);
      }
      if (this.defaultFinish && this.finish) {
        url.searchParams.set('variation_finish', this.finish.name);
      }
      if (this.selectedFinish && this.selectedFinish.name !== 'Polished Chrome') {
        url.searchParams.set('variation_finish', this.selectedFinish.name);
      }

      return url.toString();
    }
  }
};
</script>

<style lang="scss">
@import '../../scss/variables';
@import '../../scss/system';

product-card {
  visibility: hidden;
}

.product-card {
  &:hover .product-card-actions {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .fade-enter-active {
    transition: opacity 500ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  .fade-leave-active {
    transition: opacity 1s cubic-bezier(0.55, 0.055, 0.675, 0.19);
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .product-card-title {
    display: flex;
    flex-direction: column;
    gap: 0.25em;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
    }

    .finish-name {
      font-weight: normal;
    }
  }

  .product-card-specs {
    position: relative;
    font-variant-numeric: tabular-nums;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1em;

    &:not(:last-child) {
      margin-bottom: 1em;
    }
  }

  .product-card-content > :last-child {
    margin-bottom: 0;
  }

  .product-card-content > .product-card-specs:last-child {
    margin-top: 0;
  }

  .product-card-description {
    color: $color-gray-700;
    margin-bottom: 10px;
    flex: 1 1 auto;
    align-items: flex-start;
  }

  .product-material-number {
    color: $color-gray-700;
  }

  .product-card-part-info {
    margin-top: 2px;
    font-size: 14px;
    color: $color-gray-500;
  }

  .product-card-part-price {
    margin-top: 5px;
  }

  .restriction-messages {
    display: none;
  }

  .price-info--price-wrapper {
    position: relative;
    width: 100%;
  }

  .product-card-purchase-badge-wrapper {
    &:hover {
      .product-card-purchase-badge {
        opacity: 1;
        transform: translateX(50%) translateY(0) scale(1);

        @media (min-width: 768px) {
          transform: translateX(0) translateY(0) scale(1);
        }
      }
    }
  }

  .product-card-purchase-badge {
    position: absolute;
    top: -32px;
    right: 50%;
    background-color: $color-gray-900;
    color: $color-white;
    padding: 2px 8px;
    border-radius: 4px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transform: translateX(50%) translateY(10px) scale(0.9);
    transition: 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
    transition-property: opacity, transform;

    @media (min-width: 768px) {
      right: 0;
      transform: translateX(0) translateY(10px) scale(0.9);
    }
  }
}
</style>

<style lang="scss" scoped>
:deep(.slo-modal-content) {
  .slo-add-to-cart--wrapper.subscribable {
    max-width: none;
  }
}
</style>
